/**
 * Cloudinary Upload Widget Component
 *
 * Uses Cloudinary's Upload Widget for easy image uploads.
 * No backend required - uploads directly to Cloudinary.
 */

import { useEffect, useRef, useCallback } from 'react';
import { Button, BlockStack, InlineStack, Thumbnail, Text, Box } from '@shopify/polaris';
import { PlusIcon, DeleteIcon } from '@shopify/polaris-icons';

declare global {
  interface Window {
    cloudinary: {
      createUploadWidget: (
        options: CloudinaryWidgetOptions,
        callback: (error: Error | null, result: CloudinaryWidgetResult) => void
      ) => CloudinaryWidget;
    };
  }
}

interface CloudinaryWidgetOptions {
  cloudName: string;
  uploadPreset: string;
  sources?: string[];
  multiple?: boolean;
  maxFiles?: number;
  cropping?: boolean;
  croppingAspectRatio?: number;
  showAdvancedOptions?: boolean;
  folder?: string;
  resourceType?: string;
  clientAllowedFormats?: string[];
  maxFileSize?: number;
  styles?: {
    palette?: Record<string, string>;
  };
}

interface CloudinaryWidget {
  open: () => void;
  close: () => void;
  destroy: () => void;
}

interface CloudinaryWidgetResult {
  event: string;
  info: {
    secure_url: string;
    public_id: string;
    format: string;
    width: number;
    height: number;
    bytes: number;
    thumbnail_url: string;
  };
}

export interface UploadedImage {
  url: string;
  publicId: string;
  thumbnailUrl: string;
}

interface CloudinaryUploadProps {
  /** Array of uploaded image URLs */
  images: Array<{
    imageUrl: string;
    isPrimary: boolean;
    colorVariant: string;
    displayOrder: number;
    id?: string;
  }>;
  /** Callback when images change */
  onImagesChange: (images: Array<{
    imageUrl: string;
    isPrimary: boolean;
    colorVariant: string;
    displayOrder: number;
    id?: string;
  }>) => void;
  /** Maximum number of images allowed */
  maxImages?: number;
  /** Folder in Cloudinary to store images */
  folder?: string;
  /** Callback when an image is removed (for existing images with IDs) */
  onImageRemove?: (imageId: string) => void;
  /** Callback to update color variant */
  onColorVariantChange?: (index: number, color: string) => void;
}

// Load Cloudinary script
const loadCloudinaryScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.cloudinary) {
      resolve();
      return;
    }

    const existingScript = document.getElementById('cloudinary-widget-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      return;
    }

    const script = document.createElement('script');
    script.id = 'cloudinary-widget-script';
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Cloudinary widget'));
    document.body.appendChild(script);
  });
};

export function CloudinaryUpload({
  images,
  onImagesChange,
  maxImages = 10,
  folder = 'gifting-products',
  onImageRemove,
  onColorVariantChange,
}: CloudinaryUploadProps) {
  const widgetRef = useRef<CloudinaryWidget | null>(null);
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

  useEffect(() => {
    loadCloudinaryScript().catch(console.error);

    return () => {
      if (widgetRef.current) {
        widgetRef.current.destroy();
      }
    };
  }, []);

  const openWidget = useCallback(async () => {
    if (!cloudName || !uploadPreset) {
      console.error('Cloudinary configuration missing. Set VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET');
      alert('Image upload not configured. Please contact admin.');
      return;
    }

    await loadCloudinaryScript();

    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }

    widgetRef.current = window.cloudinary.createUploadWidget(
      {
        cloudName,
        uploadPreset,
        sources: ['local', 'url', 'camera'],
        multiple: true,
        maxFiles: remainingSlots,
        folder,
        resourceType: 'image',
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        maxFileSize: 10000000, // 10MB
        cropping: false,
        showAdvancedOptions: false,
        styles: {
          palette: {
            window: '#FFFFFF',
            windowBorder: '#90A0B3',
            tabIcon: '#0078FF',
            menuIcons: '#5A616A',
            textDark: '#000000',
            textLight: '#FFFFFF',
            link: '#0078FF',
            action: '#FF620C',
            inactiveTabIcon: '#0E2F5A',
            error: '#F44235',
            inProgress: '#0078FF',
            complete: '#20B832',
            sourceBg: '#E4EBF1',
          },
        },
      },
      (error, result) => {
        if (error) {
          console.error('Upload error:', error);
          return;
        }

        if (result.event === 'success') {
          const newImage = {
            imageUrl: result.info.secure_url,
            isPrimary: images.length === 0, // First image is primary
            colorVariant: '',
            displayOrder: images.length,
          };
          onImagesChange([...images, newImage]);
        }
      }
    );

    widgetRef.current.open();
  }, [cloudName, uploadPreset, images, maxImages, folder, onImagesChange]);

  const handleRemoveImage = useCallback((index: number) => {
    const image = images[index];
    if (image.id && onImageRemove) {
      onImageRemove(image.id);
    } else {
      const newImages = images.filter((_, i) => i !== index);
      // If we removed the primary, make the first one primary
      if (image.isPrimary && newImages.length > 0) {
        newImages[0].isPrimary = true;
      }
      onImagesChange(newImages);
    }
  }, [images, onImagesChange, onImageRemove]);

  const handleSetPrimary = useCallback((index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    onImagesChange(newImages);
  }, [images, onImagesChange]);

  const handleColorChange = useCallback((index: number, color: string) => {
    if (onColorVariantChange) {
      onColorVariantChange(index, color);
    } else {
      const newImages = images.map((img, i) =>
        i === index ? { ...img, colorVariant: color } : img
      );
      onImagesChange(newImages);
    }
  }, [images, onImagesChange, onColorVariantChange]);

  const canAddMore = images.length < maxImages;

  return (
    <BlockStack gap="400">
      {/* Uploaded Images Grid */}
      {images.length > 0 && (
        <BlockStack gap="300">
          {images.map((img, index) => (
            <Box
              key={img.id || img.imageUrl}
              padding="300"
              background="bg-surface-secondary"
              borderRadius="200"
            >
              <InlineStack gap="400" blockAlign="center" wrap={false}>
                <Thumbnail
                  source={img.imageUrl}
                  alt={`Product image ${index + 1}`}
                  size="medium"
                />
                <Box minWidth="120px">
                  <BlockStack gap="100">
                    <input
                      type="text"
                      placeholder="Color variant (optional)"
                      value={img.colorVariant}
                      onChange={(e) => handleColorChange(index, e.target.value)}
                      style={{
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px',
                        width: '150px',
                      }}
                    />
                    {img.isPrimary && (
                      <Text as="span" variant="bodySm" tone="success">
                        Primary Image
                      </Text>
                    )}
                  </BlockStack>
                </Box>
                <InlineStack gap="200">
                  {!img.isPrimary && (
                    <Button size="slim" onClick={() => handleSetPrimary(index)}>
                      Set Primary
                    </Button>
                  )}
                  <Button
                    size="slim"
                    tone="critical"
                    icon={DeleteIcon}
                    onClick={() => handleRemoveImage(index)}
                    accessibilityLabel="Remove image"
                  />
                </InlineStack>
              </InlineStack>
            </Box>
          ))}
        </BlockStack>
      )}

      {/* Upload Button */}
      {canAddMore && (
        <Button icon={PlusIcon} onClick={openWidget}>
          Upload Images ({images.length.toString()}/{maxImages.toString()})
        </Button>
      )}

      {!canAddMore && (
        <Text as="p" variant="bodySm" tone="subdued">
          Maximum {maxImages} images reached
        </Text>
      )}
    </BlockStack>
  );
}
